<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable no-use-before-define */
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useImoveisStore } from 'dashboard/stores/imoveis';
import ImovelAPI from 'dashboard/api/imoveis';
import LeadAlertsAPI from 'dashboard/api/leadAlerts';
import ContactFavoriteImoveisAPI from 'dashboard/api/contactFavoriteImoveis';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert, useTracking } from 'dashboard/composables';
import camelcaseKeys from 'camelcase-keys';
import {
  buildPriceBreakdownRows,
  getImovelStatusLabel,
  getImovelTipoLabel,
} from 'dashboard/helper/lais/imovelPresenter';
import {
  LAIS_TRACKING_EVENTS,
  listingTrackingPayload,
} from 'dashboard/helper/lais/trackingEvents';

import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisPriceBreakdown from 'dashboard/components-next/LaisPriceBreakdown.vue';
import UnitAmenities from 'dashboard/components-next/UnitAmenities.vue';
import SimilarListings from 'dashboard/components-next/SimilarListings.vue';
import PaymentInfo from 'dashboard/components-next/PaymentInfo.vue';
import LaisFavoriteButton from 'dashboard/components-next/lais/LaisFavoriteButton.vue';
import KeyCustodyDrawer from 'dashboard/components-next/keys/KeyCustodyDrawer.vue';
import ImageLightbox from 'dashboard/components-next/gallery/ImageLightbox.vue';
import {
  consumeAnonymousFavoriteIntent,
  consumePendingIntent,
  PENDING_INTENT_KEYS,
  savePendingIntent,
} from 'dashboard/composables/lais/usePendingIntent';

import ImovelDetailNav from '../components/ImovelDetailNav.vue';
import ImovelGalleryPanel from '../components/ImovelGalleryPanel.vue';
import ImovelIdentityPanel from '../components/ImovelIdentityPanel.vue';
import ImovelPriceSummaryCard from '../components/ImovelPriceSummaryCard.vue';
import ImovelTaxFeeGovernanceCard from '../components/ImovelTaxFeeGovernanceCard.vue';
import ImovelTaxFeeSimulationCard from '../components/ImovelTaxFeeSimulationCard.vue';
import ImovelPriceEstimateCard from '../components/ImovelPriceEstimateCard.vue';
import ImovelOwnerContactCard from '../components/ImovelOwnerContactCard.vue';
import ImovelEntryAccessCard from '../components/ImovelEntryAccessCard.vue';
import ImovelOwnerContactDrawer from '../components/ImovelOwnerContactDrawer.vue';
import ImovelPublishReadinessPanel from '../components/ImovelPublishReadinessPanel.vue';
import ImovelMediaQaPanel from '../components/ImovelMediaQaPanel.vue';
import ImovelPublicationSummarySection from '../components/ImovelPublicationSummarySection.vue';
import ImovelListingVersionsSection from '../components/ImovelListingVersionsSection.vue';
import ImovelPortalSyncSection from '../components/ImovelPortalSyncSection.vue';
import ImovelHighlightCard from '../components/ImovelHighlightCard.vue';
import ImovelReportShareCard from '../components/ImovelReportShareCard.vue';
import ImovelFeaturesCard from '../components/ImovelFeaturesCard.vue';
import ImovelConversionActionsCard from '../components/ImovelConversionActionsCard.vue';

import {
  DETAIL_GROUPS,
  DETAIL_SECTIONS,
  PRIMARY_DETAIL_SECTIONS,
  SECONDARY_DETAIL_SECTIONS,
} from '../utils/imovelShowConstants';
import {
  actionErrorMessage,
  formatBRL,
  mediaQaStatusLabel,
  normalizePayload,
  normalizeSection,
} from '../utils/imovelShowFormatters';
import { useImovelGallery } from '../composables/useImovelGallery';
import { useImovelListingLifecycle } from '../composables/useImovelListingLifecycle';
import { useImovelPortalPublication } from '../composables/useImovelPortalPublication';
import { useImovelIdentity } from '../composables/useImovelIdentity';
import { useImovelEntryAccess } from '../composables/useImovelEntryAccess';
import { useImovelTaxFees } from '../composables/useImovelTaxFees';
import { useImovelPriceEstimate } from '../composables/useImovelPriceEstimate';
import { useImovelHighlight } from '../composables/useImovelHighlight';
import { useImovelOwnerContact } from '../composables/useImovelOwnerContact';

const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const currentAccount = useMapGetter('getCurrentAccount');
const store = useImoveisStore();
const { track } = useTracking();
const { t } = useI18n();
const companyName = computed(
  () =>
    currentAccount.value?.name || currentAccount.value?.account_name || 'Imudei'
);

const isLoading = ref(true);
const isDeleting = ref(false);
const showDeleteConfirmationPopup = ref(false);
const showKeyCustodyDrawer = ref(false);
const notifyLoading = ref(false);
const notifyCreated = ref(false);
const listingFavorited = ref(false);
const listingFavoriteId = ref(null);
const mediaQaLoading = ref('');
const mediaQaEvidenceDrafts = reactive({});
const mediaQaReasonDrafts = reactive({});

const rawImovel = computed(
  () => store.records.find(r => r.id === Number(route.params.id)) || {}
);
const imovel = computed(() =>
  camelcaseKeys(rawImovel.value || {}, {
    deep: true,
  })
);
const additionalAttributes = computed(
  () => imovel.value?.additionalAttributes || {}
);
const rawAdditionalAttributes = computed(
  () => rawImovel.value?.additional_attributes || {}
);

const activeSection = computed(() => normalizeSection(route.params.section));
const sectionAccountId = computed(
  () => route.params.accountId || accountId.value
);
const primaryColumnVisible = computed(() =>
  PRIMARY_DETAIL_SECTIONS.includes(activeSection.value)
);
const secondaryColumnVisible = computed(() =>
  SECONDARY_DETAIL_SECTIONS.includes(activeSection.value)
);
const primaryColumnClass = computed(() =>
  secondaryColumnVisible.value
    ? 'lg:col-span-2 flex flex-col gap-4'
    : 'lg:col-span-3 flex flex-col gap-4'
);
const secondaryColumnClass = computed(() =>
  primaryColumnVisible.value
    ? 'flex flex-col gap-5'
    : 'lg:col-span-3 grid gap-5 lg:grid-cols-2'
);
const isDetailSectionVisible = (...sections) =>
  activeSection.value === 'resumo' || sections.includes(activeSection.value);
const activeContactId = computed(() => {
  const value = Array.isArray(route.query.contact_id)
    ? route.query.contact_id[0]
    : route.query.contact_id;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
});

const detailTabs = computed(() =>
  DETAIL_SECTIONS.map(section => ({
    ...section,
    active:
      activeSection.value === section.id ||
      (section.id === 'fotos' &&
        ['video', 'video360', 'planta'].includes(activeSection.value)),
  }))
);

// Literal i18n keys per group so the linter can verify the catalog.
const detailGroupLabel = groupId => {
  switch (groupId) {
    case 'identity':
      return t('PROPERTIES.SHOW.GROUPS.IDENTITY');
    case 'listing':
      return t('PROPERTIES.SHOW.GROUPS.LISTING');
    case 'media':
      return t('PROPERTIES.SHOW.GROUPS.MEDIA');
    default:
      return '';
  }
};

const groupedDetailTabs = computed(() =>
  DETAIL_GROUPS.map(group => ({
    id: group.id,
    label: detailGroupLabel(group.id),
    tabs: group.sections
      .map(sectionId => detailTabs.value.find(tab => tab.id === sectionId))
      .filter(Boolean),
  })).filter(group => group.tabs.length)
);

const goToSection = section => {
  const routeName =
    section === 'resumo' ? 'imovel_show' : 'imovel_show_section';
  router.push({
    name: routeName,
    params: {
      accountId: sectionAccountId.value,
      id: route.params.id,
      ...(section === 'resumo' ? {} : { section }),
    },
    query: route.query,
  });
};

const ownerBusinessContext = computed(() =>
  imovel.value?.finalidade === 'aluguel' ? 'rental' : 'purchase'
);

const {
  galleryTab,
  midiaIndex,
  lightboxOpen,
  lightboxTab,
  galleryItems,
  mediaTabs,
  galleryTabs,
  activeGalleryItems,
  galleryEmptyMessage,
  mapUrl,
  openLightbox,
  openGalleryTab,
  handleLightboxChange,
  openLightboxFromFotoQuery,
} = useImovelGallery({
  imovel,
  isLoading,
  activeSection,
  goToSection,
});

const publishabilityPayload = computed(
  () =>
    imovel.value?.publishability ||
    additionalAttributes.value.publishability ||
    additionalAttributes.value.publicabilidade ||
    {}
);
const explicitPublishabilityBlockers = computed(() => {
  const blockers = publishabilityPayload.value?.blockers;
  if (!Array.isArray(blockers)) return [];

  return blockers.map((blocker, index) => {
    if (typeof blocker === 'string') {
      return { key: `blocker-${index}-${blocker}`, label: blocker };
    }

    const normalized = normalizePayload(blocker);
    return {
      key:
        normalized.code ||
        normalized.key ||
        normalized.id ||
        `blocker-${index}`,
      label:
        normalized.label ||
        normalized.message ||
        normalized.reason ||
        normalized.code ||
        t('PROPERTIES.READINESS.UNKNOWN_BLOCKER'),
    };
  });
});
const mediaPublicability = computed(
  () =>
    imovel.value?.mediaPublicability ||
    rawImovel.value?.media_publicability ||
    {}
);
const mediaPublicabilityItems = computed(() =>
  Array.isArray(mediaPublicability.value.items)
    ? mediaPublicability.value.items.map(item => normalizePayload(item))
    : []
);
const mediaPublicabilityRequirements = computed(() =>
  Array.isArray(mediaPublicability.value.requirements)
    ? mediaPublicability.value.requirements.map(item => normalizePayload(item))
    : []
);
const mediaPublicabilityBlockingMessages = computed(() => {
  const messages = mediaPublicability.value.blockingMessages;
  if (Array.isArray(messages)) return messages.filter(Boolean);

  const reasons = mediaPublicability.value.blockingReasons;
  return Array.isArray(reasons) ? reasons.filter(Boolean) : [];
});
const mediaPublicabilityStatus = computed(
  () => mediaPublicability.value.status || ''
);
const mediaReadinessStatus = computed(() => {
  if (mediaPublicabilityStatus.value === 'approved') return 'done';
  if (mediaPublicabilityStatus.value === 'pending') return 'pending';
  if (mediaPublicabilityStatus.value === 'rejected') return 'blocked';
  if (galleryItems.value.length >= 3) return 'done';
  if (galleryItems.value.length > 0) return 'pending';
  return 'blocked';
});
const mediaReadinessEvidenceLabel = computed(() => {
  if (mediaPublicability.value.totalCount !== undefined) {
    return `${mediaPublicability.value.publishableCount || 0}/${mediaPublicability.value.minPhotos || 3} aprovadas · ${mediaPublicability.value.totalCount || 0} no QA`;
  }

  return t('PROPERTIES.READINESS.MEDIA.EVIDENCE', {
    count: galleryItems.value.length,
  });
});
const mediaReadinessBlocker = computed(() => {
  if (mediaReadinessStatus.value !== 'blocked') return '';
  return (
    mediaPublicabilityBlockingMessages.value.join(' · ') ||
    t('PROPERTIES.READINESS.MEDIA.BLOCKER')
  );
});
const mediaQaCanPublish = computed(() => mediaReadinessStatus.value === 'done');

const refreshImovel = async () => {
  await store.show(route.params.id);
  await fetchListingLifecycle();
};

const onLifecycleApplied = () =>
  Promise.allSettled([refreshImovel(), fetchPortalSyncs()]);

const {
  listingLoading,
  listingError,
  lifecyclePreview,
  lifecyclePreviewLoading,
  lifecycleLoading,
  lifecycleReason,
  suspendDays,
  versionSuccessMessage,
  currentListing,
  listingStatusLabel,
  rentalAdministratorLabel,
  listingSourceLabel,
  visitasPermitidasLabel,
  listingVersionRecords,
  latestListingVersion,
  listingLifecycleTimeline,
  latestListingDiffEntries,
  lifecyclePreviewDiffEntries,
  lifecyclePreviewLabel,
  listingVersionEvidenceLabel,
  fetchListingLifecycle,
  lifecyclePreviewMatches,
  prepareOrRunLifecycleAction,
  runLifecycleAction,
} = useImovelListingLifecycle({
  imovel,
  additionalAttributes,
  mediaQaCanPublish,
  mediaReadinessBlocker,
  onLifecycleApplied,
});

const {
  selectedPortal,
  portalLoading,
  portalActionLoading,
  portalError,
  portalPermissionDenied,
  portalSuccessMessage,
  normalizedPortalSyncs,
  selectedPortalLabel,
  olxSync,
  olxConfigured,
  olxConnected,
  portalPreflightBlockers,
  portalPreflightWarnings,
  portalManualConfigurationRows,
  portalRuntimeState,
  olxConnectionLabel,
  olxSyncStatusLabel,
  olxSyncAction,
  olxLastSyncLabel,
  portalHistoryRows,
  fetchPortalStatus,
  fetchPortalPreflight,
  fetchPortalSyncs,
  openPortalConsole,
  openOlxAuthorizeUrl,
  runPortalSync,
} = useImovelPortalPublication({
  imovel,
  currentListing,
  lifecycleReason,
  activeSection,
  sectionAccountId,
  refreshImovel,
});

const {
  identityLoading,
  identityError,
  identityPermissionDenied,
  identitySuccessMessage,
  aliasForm,
  aliasSaving,
  aliasError,
  canonicalAddressLine,
  hasIdentityAddress,
  identityUsesLegacyProjection,
  identityAliases,
  identityConflicts,
  identityActionableConflicts,
  identityChangeHistory,
  identityMergeHistory,
  identitySourceLinks,
  identityCanonicalRows,
  identityEmpty,
  identityStatusLabel,
  identityStatusTone,
  canSubmitAlias,
  fetchIdentity,
  submitAlias,
} = useImovelIdentity({ imovel, portalSyncs: normalizedPortalSyncs });

const {
  entryAccessLoading,
  entryAccessSaving,
  entryAccessDeletingId,
  entryAccessError,
  entryAccessPermissionDenied,
  entryAccessSuccess,
  entryAccessForm,
  normalizedEntryAccesses,
  entryAccessEmpty,
  isEditingEntryAccess,
  entryAccessSubmitLabel,
  canSubmitEntryAccess,
  entryInformationHistory,
  selectedEntryAccessAuditLabel,
  fetchEntryAccesses,
  fetchEntryInformation,
  clearEntryAccessForm,
  editEntryAccess,
  submitEntryAccess,
  deleteEntryAccess,
} = useImovelEntryAccess();

const {
  taxFeeSimulationLoading,
  taxFeeSimulationError,
  taxFeeSimulationSuccess,
  taxFeeForm,
  taxFeeGovernanceRows,
  condominiumFeeRow,
  iptuFeeRow,
  taxFeeSimulationHistory,
  taxFeeSimulationRows,
  taxFeeSimulationTotalRows,
  calcularTaxasCustos,
} = useImovelTaxFees({
  imovel,
  additionalAttributes,
  rawAdditionalAttributes,
  refreshImovel,
});

const typeLabel = computed(() => getImovelTipoLabel(imovel.value?.tipo));
const statusLabel = computed(() => getImovelStatusLabel(imovel.value?.status));
const listingTags = computed(() =>
  Array.isArray(imovel.value?.listingTags) ? imovel.value.listingTags : []
);
const houseDetailsLabel = computed(() => {
  const area = imovel.value?.areaUtil || imovel.value?.areaTotal;
  const bedrooms = Number(imovel.value?.quartos);
  const parkingSpots = Number(imovel.value?.vagasGaragem);
  const details = [
    area ? `${area} m²` : null,
    Number.isFinite(bedrooms)
      ? `${bedrooms} ${bedrooms === 1 ? 'quarto' : 'quartos'}`
      : null,
    Number.isFinite(parkingSpots)
      ? `${parkingSpots} ${parkingSpots === 1 ? 'vaga' : 'vagas'}`
      : null,
  ].filter(Boolean);

  return details.join(' · ');
});
const addressLabel = computed(() => {
  const address = [
    imovel.value?.endereco,
    imovel.value?.bairro,
    imovel.value?.cidade,
    imovel.value?.uf,
  ]
    .filter(Boolean)
    .join(', ');

  return address || 'Sem informações do endereço';
});
const displayedPriceRows = computed(() =>
  buildPriceBreakdownRows(imovel.value, {
    rows: imovel.value?.priceBreakdown,
    countryCode: 'BR',
    companyName: companyName.value,
  })
);
const recommendedValue = computed(() =>
  imovel.value?.finalidade === 'aluguel'
    ? imovel.value?.valorAluguel
    : imovel.value?.valorVenda
);
const recommendedValueLabel = computed(
  () => formatBRL(recommendedValue.value) || 'valor a definir'
);
const showPaymentInfo = computed(
  () =>
    imovel.value?.finalidade === 'venda' ||
    imovel.value?.ofereceFinanciamento ||
    Boolean(imovel.value?.valorVenda)
);
const reportLink = computed(() => {
  if (imovel.value?.linkExterno) return imovel.value.linkExterno;
  if (typeof window === 'undefined') return '';

  return `${window.location.origin}/app/accounts/${accountId.value}/imoveis/${route.params.id}`;
});
const agentName = computed(
  () =>
    currentUser.value?.name ||
    currentUser.value?.display_name ||
    currentUser.value?.email ||
    'time Imudei'
);

const {
  priceEstimate,
  priceEstimateLoading,
  priceEstimateApplying,
  priceEstimateError,
  priceApplyReason,
  priceEstimateSource,
  priceEstimateConfidenceLabel,
  priceEstimateSamples,
  estimatedPriceValue,
  priceEstimateRangeLabel,
  priceEstimateDateLabel,
  priceEstimateEmptyMessage,
  hasApplicablePriceEstimate,
  priceApplicationHistory,
  canApplyPriceEstimate,
  priceEstimateConfirmDescription,
  solicitarEstimativaPreco,
  aplicarEstimativaPreco,
} = useImovelPriceEstimate({
  imovel,
  additionalAttributes,
  rawAdditionalAttributes,
  recommendedValue,
  refreshImovel,
});

const showPriceEstimateConfirm = ref(false);

const openPriceEstimateConfirm = () => {
  if (!canApplyPriceEstimate()) return;

  showPriceEstimateConfirm.value = true;
};

const closePriceEstimateConfirm = () => {
  showPriceEstimateConfirm.value = false;
};

const confirmPriceEstimateApplication = async () => {
  await aplicarEstimativaPreco();
  closePriceEstimateConfirm();
};

const trackListingAction = (eventName, properties = {}) => {
  if (!accountId.value || !imovel.value?.id) return;

  track(
    eventName,
    listingTrackingPayload(imovel.value, {
      accountId: accountId.value,
      sourceList: 'imovel_show',
      contactId: activeContactId.value,
      ...properties,
    })
  );
};

const {
  canManageHighlight,
  highlightForm,
  highlightSubmitting,
  showHighlightConfirm,
  highlightError,
  highlightMinDateTime,
  highlightSchedule,
  highlightStatusTone,
  highlightStatusLabel,
  highlightPlacementLabel,
  highlightScheduledForLabel,
  highlightProviderName,
  isHighlightFormValid,
  highlightConfirmDescription,
  openHighlightConfirm,
  closeHighlightConfirm,
  submitHighlight,
} = useImovelHighlight({ imovel, refreshImovel, trackListingAction });

const {
  ownerContact,
  ownerContactError,
  ownerContactLoading,
  ownerRevealLoading,
  showOwnerContactDrawer,
  ownerContactStep,
  ownerRevealForm,
  ownerWeeklyCount,
  ownerWeeklyLimit,
  ownerWeeklyCountLabel,
  ownerLimitReached,
  ownerContactPhone,
  ownerContactEmail,
  ownerContactWhatsappMessage,
  ownerWhatsappHref,
  ownerPhoneHref,
  fetchOwnerContactStats,
  revealOwnerContact,
  openOwnerContactDrawer,
  closeOwnerContactDrawer,
  acceptOwnerContactTerms,
  backOwnerContactStep,
  selectOwnerContactReason,
  trackOwnerContactChannel,
} = useImovelOwnerContact({
  imovel,
  ownerBusinessContext,
  companyName,
  agentName,
  addressLabel,
  reportLink,
});

const publishableReviewRows = computed(() =>
  [
    { label: 'Título', value: imovel.value?.titulo },
    { label: 'Tipo', value: typeLabel.value },
    { label: 'Finalidade', value: imovel.value?.finalidade },
    { label: 'Endereço público', value: addressLabel.value },
    { label: 'Preço principal', value: recommendedValueLabel.value },
    {
      label: 'Condomínio',
      value: formatBRL(condominiumFeeRow.value?.canonicalValue),
    },
    { label: 'IPTU', value: formatBRL(iptuFeeRow.value?.canonicalValue) },
    { label: 'Mídias', value: `${galleryItems.value.length} item(ns)` },
    { label: 'Status interno', value: listingStatusLabel.value },
  ].filter(row => row.value)
);

const portalReadinessStatus = computed(() => {
  if (portalRuntimeState.value === 'configured') return 'done';
  if (portalRuntimeState.value === 'degraded') return 'pending';
  return 'blocked';
});
const portalReadinessBlocker = computed(() => {
  if (portalReadinessStatus.value === 'done') return '';
  if (portalRuntimeState.value === 'invalid') {
    return 'Credencial inválida ou configuração incompleta no portal.';
  }
  if (portalRuntimeState.value === 'degraded') {
    return 'Portal com reconciliação pendente ou estado remoto degradado.';
  }
  return t('PROPERTIES.READINESS.PORTAL.BLOCKER');
});
const identityReadyWithoutConflict = computed(
  () => hasIdentityAddress.value && !identityActionableConflicts.value.length
);
const identityReadinessStatus = computed(() => {
  if (!identityReadyWithoutConflict.value) return 'blocked';
  if (identityUsesLegacyProjection.value) return 'pending';
  return 'done';
});
const identityReadinessBlocker = computed(() => {
  if (!identityReadyWithoutConflict.value) {
    return t('PROPERTIES.READINESS.IDENTITY.BLOCKER');
  }
  if (identityUsesLegacyProjection.value) {
    return 'Identidade física ainda baseada nos campos legados.';
  }
  return '';
});
const publishabilityChecklistItems = computed(() => [
  {
    id: 'identity',
    label: t('PROPERTIES.READINESS.IDENTITY.LABEL'),
    status: identityReadinessStatus.value,
    evidenceLabel: canonicalAddressLine.value || addressLabel.value,
    blocker: identityReadinessBlocker.value,
    actionLabel: t('PROPERTIES.READINESS.ACTIONS.OPEN'),
    section: 'endereco',
  },
  {
    id: 'commercial_listing',
    label: t('PROPERTIES.READINESS.LISTING.LABEL'),
    status:
      imovel.value?.finalidade && recommendedValue.value ? 'done' : 'blocked',
    evidenceLabel: `${listingStatusLabel.value} · ${recommendedValueLabel.value}`,
    blocker:
      imovel.value?.finalidade && recommendedValue.value
        ? ''
        : t('PROPERTIES.READINESS.LISTING.BLOCKER'),
    actionLabel: t('PROPERTIES.READINESS.ACTIONS.OPEN'),
    section: 'valores',
  },
  {
    id: 'media',
    label: t('PROPERTIES.READINESS.MEDIA.LABEL'),
    status: mediaReadinessStatus.value,
    evidenceLabel: mediaReadinessEvidenceLabel.value,
    blocker: mediaReadinessBlocker.value,
    actionLabel: t('PROPERTIES.READINESS.ACTIONS.OPEN'),
    section: 'fotos',
  },
  {
    id: 'portal',
    label: t('PROPERTIES.READINESS.PORTAL.LABEL'),
    status: portalReadinessStatus.value,
    evidenceLabel: `${olxConnectionLabel.value} · ${olxSyncStatusLabel.value}`,
    blocker: portalReadinessBlocker.value,
    actionLabel:
      portalReadinessStatus.value === 'done'
        ? t('PROPERTIES.READINESS.ACTIONS.OPEN')
        : t('PROPERTIES.READINESS.ACTIONS.CONFIGURE_PORTAL'),
    section: 'publicacao',
    action:
      portalReadinessStatus.value === 'done'
        ? 'open_publication'
        : 'open_portal_console',
  },
  {
    id: 'version',
    label: t('PROPERTIES.READINESS.VERSION.CHECK_LABEL'),
    status: listingVersionRecords.value.length ? 'done' : 'pending',
    evidenceLabel: listingVersionEvidenceLabel.value,
    blocker: listingVersionRecords.value.length
      ? ''
      : t('PROPERTIES.READINESS.VERSION.BLOCKER'),
    actionLabel: t('PROPERTIES.READINESS.ACTIONS.OPEN'),
    section: 'publicacao',
  },
  {
    id: 'access',
    label: t('PROPERTIES.READINESS.ACCESS.LABEL'),
    status: imovel.value?.visitasPermitidas ? 'done' : 'pending',
    evidenceLabel: visitasPermitidasLabel.value,
    blocker: imovel.value?.visitasPermitidas
      ? ''
      : t('PROPERTIES.READINESS.ACCESS.BLOCKER'),
    actionLabel: t('PROPERTIES.READINESS.ACTIONS.OPEN'),
    section: 'proprietario',
  },
]);
const publishabilityBlockedCount = computed(
  () =>
    publishabilityChecklistItems.value.filter(item => item.status === 'blocked')
      .length + explicitPublishabilityBlockers.value.length
);
const publishabilityPendingCount = computed(
  () =>
    publishabilityChecklistItems.value.filter(item => item.status === 'pending')
      .length
);
const publishabilitySummary = computed(() => {
  if (publishabilityBlockedCount.value > 0) {
    return {
      tone: 'danger',
      label: t('PROPERTIES.READINESS.SUMMARY.BLOCKED', {
        count: publishabilityBlockedCount.value,
      }),
    };
  }
  if (publishabilityPendingCount.value > 0) {
    return {
      tone: 'warning',
      label: t('PROPERTIES.READINESS.SUMMARY.PENDING', {
        count: publishabilityPendingCount.value,
      }),
    };
  }
  return {
    tone: 'positive',
    label: t('PROPERTIES.READINESS.SUMMARY.READY'),
  };
});

const carregar = async () => {
  isLoading.value = true;
  try {
    await store.show(route.params.id);
    await Promise.allSettled([
      fetchListingLifecycle(),
      fetchIdentity(),
      fetchOwnerContactStats(),
      fetchPortalStatus(),
      fetchPortalPreflight(),
      fetchPortalSyncs(),
      fetchEntryAccesses(),
      fetchEntryInformation(),
    ]);
    openLightboxFromFotoQuery();
    await applyPendingIntents();
    trackImovelView();
  } finally {
    isLoading.value = false;
  }
};

const editar = () =>
  router.push(
    frontendURL(`accounts/${accountId.value}/imoveis/${route.params.id}/edit`)
  );

const openDeletePopup = () => {
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const excluir = async () => {
  isDeleting.value = true;
  try {
    await store.delete(route.params.id);
    useAlert('Imóvel excluído');
    closeDeletePopup();
    router.push(frontendURL(`accounts/${accountId.value}/imoveis`));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || 'Não foi possível excluir o imóvel.'
    );
  } finally {
    isDeleting.value = false;
  }
};

const openIdentityDuplicateQueue = () => {
  router.push({
    name: 'imoveis_duplicate_candidates',
    params: { accountId: sectionAccountId.value },
    query: { imovel_id: route.params.id },
  });
};

const openKeyCustodyDrawer = () => {
  showKeyCustodyDrawer.value = true;
};

const handleKeyCustodyChanged = () => {
  useAlert('Ledger de chaves atualizado.');
};

const handleReadinessAction = item => {
  if (item?.action === 'open_portal_console') {
    openPortalConsole();
    return;
  }

  if (item?.section) {
    goToSection(item.section);
  }
};

const updateMediaQaDecision = async (item, status) => {
  mediaQaLoading.value = `${item.id}:${status}`;
  try {
    await ImovelAPI.updateMediaQa(route.params.id, {
      items: [
        {
          id: item.id,
          qa_status: status,
          evidence: mediaQaEvidenceDrafts[item.id] || item.qa?.evidence || '',
          reason: mediaQaReasonDrafts[item.id] || item.qa?.reason || '',
          cover: item.cover,
          sort_order: item.sortOrder,
          alt_text: item.altText,
        },
      ],
    });
    await refreshImovel();
    useAlert(`QA de mídia ${mediaQaStatusLabel(status).toLowerCase()}.`);
  } catch (error) {
    useAlert(
      actionErrorMessage(error, 'Não foi possível salvar o QA da mídia.')
    );
  } finally {
    mediaQaLoading.value = '';
  }
};

const whatsappReportMessage = computed(
  () =>
    `Olá, proprietário! Aqui é ${agentName.value}, da ${companyName.value}, tudo bem?\n\n` +
    `Preparei essa análise do melhor valor para o seu imóvel com base no que temos visto no mercado imobiliário da região. Levando em conta as características dele, o preço recomendado é de ${recommendedValueLabel.value}.\n\n` +
    `Neste link tem mais detalhes: ${reportLink.value}`
);

const copiarLinkAvaliacao = async () => {
  try {
    await navigator.clipboard.writeText(reportLink.value);
    useAlert('Link copiado com sucesso!');
  } catch (e) {
    useAlert('Não foi possível copiar o link');
  }
};

const compartilharAvaliacaoWhatsapp = () => {
  if (typeof window === 'undefined') return;

  window.open(
    `https://wa.me/?text=${encodeURIComponent(whatsappReportMessage.value)}`,
    '_blank',
    'noopener'
  );
};

const handleListingShared = payload => {
  trackListingAction(LAIS_TRACKING_EVENTS.LISTING_SHARE_CLICKED, {
    shareMethod: payload.method,
    medium: payload.medium,
    sharedUrl: payload.url,
  });
  useAlert('Link do imóvel pronto para compartilhar.');
};

const handleListingShareError = () => {
  useAlert('Não foi possível compartilhar este imóvel.');
};

const handleMortgageOpen = payload => {
  trackListingAction('listing.mortgage_link_clicked', payload);
};

const createAvailabilityAlert = async () => {
  if (notifyLoading.value || notifyCreated.value) return;

  if (!activeContactId.value) {
    savePendingIntent(PENDING_INTENT_KEYS.alert, {
      listingId: imovel.value?.id,
      businessContext: ownerBusinessContext.value,
      relistingStatus: imovel.value?.status,
    });
    useAlert('Abra este imóvel a partir de um cliente para criar o alerta.');
    return;
  }

  notifyLoading.value = true;
  try {
    const request = LeadAlertsAPI.create({
      contact_id: activeContactId.value,
      title: `Avisar disponibilidade: ${imovel.value?.titulo || route.params.id}`,
      finalidade: imovel.value?.finalidade,
      cidade: imovel.value?.cidade,
      bairro: imovel.value?.bairro,
      tipo: imovel.value?.tipo,
      status: 'active',
      filters: {
        imovel_id: imovel.value?.id,
        alert_kind: 'notify_when_available',
        source: 'imovel_show',
        campaign: 'VISITS_ALLOWED',
        relisting_status: imovel.value?.status,
        business_context: ownerBusinessContext.value,
      },
    });
    await Promise.all([
      request,
      new Promise(resolve => {
        window.setTimeout(resolve, 6000);
      }),
    ]);
    notifyCreated.value = true;
    trackListingAction('listing.notify_when_available_clicked');
    useAlert('Você será avisado quando o imóvel estiver disponível.');
  } catch (error) {
    useAlert('Não foi possível criar o alerta agora.');
  } finally {
    notifyLoading.value = false;
  }
};

const goToSimilarListings = () => {
  trackListingAction('listing.similar_clicked');
  router.push({
    name: 'imoveis_index',
    params: { accountId: accountId.value },
    query: {
      similar_to: route.params.id,
      business_context: ownerBusinessContext.value,
    },
  });
};

const agendarVisita = () => {
  trackListingAction('listing.schedule_visit_clicked');
  router.push({
    path: frontendURL(`accounts/${accountId.value}/visitas/new`),
    query: { imovel_id: route.params.id },
  });
};

const contactAdvertiser = () => {
  trackListingAction('listing.contact_advertiser_clicked');
  openOwnerContactDrawer();
};

const handleListingCtaAction = cta => {
  const actions = {
    schedule_visit: agendarVisita,
    contact_advertiser: contactAdvertiser,
    notify_when_available: createAvailabilityAlert,
    similar_properties: goToSimilarListings,
  };
  actions[cta.action]?.();
};

const handleFavoriteToggle = payload => {
  listingFavorited.value = Boolean(payload.status);
  listingFavoriteId.value = payload.favoriteId;
  track('favorite.toggled', {
    listingId: payload.listingId,
    status: payload.status,
    position: payload.position,
    businessContext: payload.businessContext,
  });
};

const intentListingId = intent => intent?.listingId || intent?.imovelId;

const isCurrentListingIntent = intent =>
  intentListingId(intent) &&
  String(intentListingId(intent)) === String(imovel.value?.id);

const applyPendingFavorite = async intent => {
  if (!isCurrentListingIntent(intent) || !activeContactId.value) return;
  if (listingFavorited.value) return;

  try {
    const { data } = await ContactFavoriteImoveisAPI.create(
      activeContactId.value,
      {
        imovel_id: imovel.value.id,
        list_name: 'default',
      }
    );
    listingFavorited.value = true;
    listingFavoriteId.value = data?.id || data?.favorite?.id || null;
    track('favorite.toggled', {
      listingId: imovel.value.id,
      status: true,
      position: 'pending_resume',
      businessContext: intent.businessContext || ownerBusinessContext.value,
    });
  } catch {
    useAlert('Não foi possível salvar o favorito pendente.');
  }
};

const applyPendingIntents = async () => {
  if (!activeContactId.value || !imovel.value?.id) return;

  const pendingFavorite = consumePendingIntent(PENDING_INTENT_KEYS.favorite);
  const pendingAnonymousFavorite = consumeAnonymousFavoriteIntent();
  const pendingAlert = consumePendingIntent(PENDING_INTENT_KEYS.alert);

  await applyPendingFavorite(pendingFavorite);
  await applyPendingFavorite(pendingAnonymousFavorite);

  if (isCurrentListingIntent(pendingAlert)) {
    createAvailabilityAlert();
  }
};

const trackImovelView = () => {
  if (!accountId.value || !imovel.value?.id) return;

  track(
    LAIS_TRACKING_EVENTS.LISTING_VIEWED,
    listingTrackingPayload(imovel.value, {
      accountId: accountId.value,
      sourceList: 'imovel_show',
    })
  );
};

watch(
  () => imovel.value?.id,
  () => {
    listingFavoriteId.value =
      imovel.value?.favoriteId || imovel.value?.favorite_id || null;
    listingFavorited.value = Boolean(
      imovel.value?.favorite ||
        imovel.value?.favorito ||
        listingFavoriteId.value
    );
    notifyCreated.value = false;
  },
  { immediate: true }
);

onMounted(carregar);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex flex-col h-full bg-lais-bg-page font-redHatText overflow-hidden"
  >
    <LaisHeader
      :title="imovel?.titulo || 'Carregando…'"
      :helper="
        imovel?.bairro || imovel?.cidade
          ? `${imovel?.bairro || ''}${imovel?.bairro && imovel?.cidade ? ', ' : ''}${imovel?.cidade || ''}`
          : 'Localização não informada'
      "
    >
      <template #actions>
        <LaisFavoriteButton
          :listing="imovel"
          :contact-id="activeContactId"
          position="app_bar"
          :business-context="ownerBusinessContext"
          :initially-favorited="listingFavorited"
          :initial-favorite-id="listingFavoriteId"
          @toggle="handleFavoriteToggle"
        />
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-pencil"
          @click="editar"
        >
          Editar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-key-round"
          @click="openKeyCustodyDrawer"
        >
          Chaves
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-trash-2"
          :is-loading="isDeleting"
          @click="openDeletePopup"
        >
          Excluir
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="primary"
          icon="i-lucide-calendar-plus"
          @click="agendarVisita"
        >
          Agendar visita
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <ImovelDetailNav
      v-if="!isLoading && imovel?.id"
      :grouped-detail-tabs="groupedDetailTabs"
      :status-label="statusLabel"
      :listing-tags="listingTags"
      :house-details-label="houseDetailsLabel"
      @select-section="goToSection"
    />

    <div v-if="!isLoading && imovel" class="flex-1 overflow-auto">
      <div
        class="grid w-full gap-6 px-4 py-6 sm:px-6 lg:grid-cols-3 lg:px-10 lg:py-8"
      >
        <!-- Galeria e identidade -->
        <div v-if="primaryColumnVisible" :class="primaryColumnClass">
          <ImovelGalleryPanel
            v-if="
              isDetailSectionVisible(
                'fotos',
                'mapa',
                'video',
                'video360',
                'planta'
              )
            "
            v-model:gallery-tab="galleryTab"
            v-model:midia-index="midiaIndex"
            :gallery-tabs="galleryTabs"
            :media-tabs="mediaTabs"
            :active-gallery-items="activeGalleryItems"
            :gallery-empty-message="galleryEmptyMessage"
            :map-url="mapUrl"
            :imovel="imovel"
            :contact-id="activeContactId"
            :business-context="ownerBusinessContext"
            :favorited="listingFavorited"
            :favorite-id="listingFavoriteId"
            @open-lightbox="openLightbox"
            @select-gallery-tab="openGalleryTab"
            @toggle-favorite="handleFavoriteToggle"
          />

          <LaisCard
            v-if="activeSection === 'resumo' && imovel.descricao"
            title="Sobre o imóvel"
          >
            <p
              class="text-[14px] leading-relaxed text-lais-text-body whitespace-pre-wrap"
            >
              {{ imovel.descricao }}
            </p>
          </LaisCard>

          <UnitAmenities
            v-if="isDetailSectionVisible('caracteristicas')"
            :imovel="imovel"
          />

          <ImovelIdentityPanel
            v-if="isDetailSectionVisible('identidade', 'endereco')"
            v-model:alias-form="aliasForm"
            :status-label="identityStatusLabel"
            :status-tone="identityStatusTone"
            :success-message="identitySuccessMessage"
            :loading="identityLoading"
            :permission-denied="identityPermissionDenied"
            :error="identityError"
            :empty="identityEmpty"
            :actionable-conflicts="identityActionableConflicts"
            :canonical-address-line="canonicalAddressLine"
            :uses-legacy-projection="identityUsesLegacyProjection"
            :canonical-rows="identityCanonicalRows"
            :aliases="identityAliases"
            :source-links="identitySourceLinks"
            :conflicts="identityConflicts"
            :change-history="identityChangeHistory"
            :merge-history="identityMergeHistory"
            :alias-error="aliasError"
            :alias-saving="aliasSaving"
            :can-submit-alias="canSubmitAlias"
            @reload="fetchIdentity"
            @open-duplicate-queue="openIdentityDuplicateQueue"
            @submit-alias="submitAlias"
          />
        </div>

        <!-- Abas operacionais -->
        <aside v-if="secondaryColumnVisible" :class="secondaryColumnClass">
          <ImovelPriceSummaryCard
            v-if="isDetailSectionVisible('valores', 'taxas')"
            :imovel="imovel"
            :condominium-fee-row="condominiumFeeRow"
            :iptu-fee-row="iptuFeeRow"
          />

          <ImovelTaxFeeGovernanceCard
            v-if="isDetailSectionVisible('taxas')"
            :rows="taxFeeGovernanceRows"
          />

          <ImovelTaxFeeSimulationCard
            v-if="isDetailSectionVisible('taxas')"
            v-model:tax-fee-form="taxFeeForm"
            :loading="taxFeeSimulationLoading"
            :success-message="taxFeeSimulationSuccess"
            :error-message="taxFeeSimulationError"
            :rows="taxFeeSimulationRows"
            :total-rows="taxFeeSimulationTotalRows"
            :history="taxFeeSimulationHistory"
            @calculate="calcularTaxasCustos"
          />

          <LaisPriceBreakdown
            v-if="
              isDetailSectionVisible('valores', 'taxas') &&
              displayedPriceRows.length
            "
            :rows="displayedPriceRows"
            start-expanded
          />

          <ImovelPriceEstimateCard
            v-if="isDetailSectionVisible('valores')"
            v-model:price-apply-reason="priceApplyReason"
            :estimate="priceEstimate"
            :loading="priceEstimateLoading"
            :applying="priceEstimateApplying"
            :error-message="priceEstimateError"
            :empty-message="priceEstimateEmptyMessage"
            :has-applicable="hasApplicablePriceEstimate"
            :estimated-value="estimatedPriceValue"
            :range-label="priceEstimateRangeLabel"
            :source-label="priceEstimateSource"
            :confidence-label="priceEstimateConfidenceLabel"
            :samples="priceEstimateSamples"
            :date-label="priceEstimateDateLabel"
            :application-history="priceApplicationHistory"
            @request="solicitarEstimativaPreco"
            @apply="openPriceEstimateConfirm"
          />

          <PaymentInfo
            v-if="isDetailSectionVisible('valores', 'taxas') && showPaymentInfo"
            :imovel="imovel"
            :account-id="accountId"
            @mortgage="handleMortgageOpen"
          />

          <ImovelOwnerContactCard
            v-if="isDetailSectionVisible('proprietario')"
            :weekly-count="ownerWeeklyCount"
            :weekly-limit="ownerWeeklyLimit"
            :limit-reached="ownerLimitReached"
            :has-revealed-contact="!!ownerContact"
            :loading="ownerContactLoading"
            @open="openOwnerContactDrawer"
          />

          <ImovelEntryAccessCard
            v-if="isDetailSectionVisible('acessos')"
            v-model:entry-access-form="entryAccessForm"
            :audit-label="selectedEntryAccessAuditLabel"
            :accesses="normalizedEntryAccesses"
            :success-message="entryAccessSuccess"
            :permission-denied="entryAccessPermissionDenied"
            :error-message="entryAccessError"
            :loading="entryAccessLoading"
            :empty="entryAccessEmpty"
            :deleting-id="entryAccessDeletingId"
            :is-editing="isEditingEntryAccess"
            :submit-label="entryAccessSubmitLabel"
            :can-submit="canSubmitEntryAccess"
            :saving="entryAccessSaving"
            :information-history="entryInformationHistory"
            @edit="editEntryAccess"
            @remove="deleteEntryAccess"
            @clear-form="clearEntryAccessForm"
            @submit="submitEntryAccess"
            @open-ledger="openKeyCustodyDrawer"
          />

          <ImovelOwnerContactDrawer
            v-model:owner-reveal-form="ownerRevealForm"
            :show="showOwnerContactDrawer"
            :step="ownerContactStep"
            :weekly-count-label="ownerWeeklyCountLabel"
            :limit-reached="ownerLimitReached"
            :contact-loading="ownerContactLoading"
            :reveal-loading="ownerRevealLoading"
            :company-name="companyName"
            :contact="ownerContact"
            :contact-phone="ownerContactPhone"
            :contact-email="ownerContactEmail"
            :whatsapp-href="ownerWhatsappHref"
            :phone-href="ownerPhoneHref"
            :whatsapp-message="ownerContactWhatsappMessage"
            :error-message="ownerContactError"
            @close="closeOwnerContactDrawer"
            @accept-terms="acceptOwnerContactTerms"
            @back="backOwnerContactStep"
            @reveal="revealOwnerContact"
            @select-reason="selectOwnerContactReason"
            @track-channel="trackOwnerContactChannel"
          />

          <ImovelPublishReadinessPanel
            v-if="isDetailSectionVisible('publicacao', 'versionamento')"
            :summary="publishabilitySummary"
            :items="publishabilityChecklistItems"
            :explicit-blockers="explicitPublishabilityBlockers"
            @action="handleReadinessAction"
          />

          <ImovelMediaQaPanel
            v-if="
              isDetailSectionVisible('fotos') &&
              (mediaPublicabilityItems.length ||
                mediaPublicabilityRequirements.length)
            "
            v-model:media-qa-reason-drafts="mediaQaReasonDrafts"
            v-model:media-qa-evidence-drafts="mediaQaEvidenceDrafts"
            :status="mediaPublicabilityStatus"
            :evidence-label="mediaReadinessEvidenceLabel"
            :requirements="mediaPublicabilityRequirements"
            :items="mediaPublicabilityItems"
            :loading-key="mediaQaLoading"
            @decide="updateMediaQaDecision"
          />

          <LaisCard
            v-if="isDetailSectionVisible('publicacao', 'versionamento')"
            title="Publicação, portais e versões"
            density="cozy"
          >
            <ImovelPublicationSummarySection
              v-model:lifecycle-reason="lifecycleReason"
              v-model:suspend-days="suspendDays"
              :summary="publishabilitySummary"
              :review-rows="publishableReviewRows"
              :listing-status-label="listingStatusLabel"
              :rental-administrator-label="rentalAdministratorLabel"
              :listing-source-label="listingSourceLabel"
              :visitas-permitidas-label="visitasPermitidasLabel"
              :lifecycle-loading="lifecycleLoading"
              :media-qa-can-publish="mediaQaCanPublish"
              :publish-preview-ready="lifecyclePreviewMatches('publish')"
              @prepare-action="prepareOrRunLifecycleAction"
              @run-action="runLifecycleAction"
            />

            <ImovelListingVersionsSection
              :listing-loading="listingLoading"
              :preview-loading="lifecyclePreviewLoading"
              :preview-label="lifecyclePreviewLabel"
              :listing-error="listingError"
              :success-message="versionSuccessMessage"
              :preview="lifecyclePreview"
              :preview-diff-entries="lifecyclePreviewDiffEntries"
              :latest-version="latestListingVersion"
              :version-evidence-label="listingVersionEvidenceLabel"
              :latest-diff-entries="latestListingDiffEntries"
              :timeline="listingLifecycleTimeline"
            />

            <ImovelPortalSyncSection
              v-model:selected-portal="selectedPortal"
              :portal-loading="portalLoading"
              :configured="olxConfigured"
              :connected="olxConnected"
              :connection-label="olxConnectionLabel"
              :portal-label="selectedPortalLabel"
              :manual-rows="portalManualConfigurationRows"
              :blockers="portalPreflightBlockers"
              :warnings="portalPreflightWarnings"
              :sync-status-label="olxSyncStatusLabel"
              :sync="olxSync"
              :last-sync-label="olxLastSyncLabel"
              :success-message="portalSuccessMessage"
              :permission-denied="portalPermissionDenied"
              :error-message="portalError"
              :action-loading="portalActionLoading"
              :sync-action="olxSyncAction"
              :history-rows="portalHistoryRows"
              @open-console="openPortalConsole"
              @authorize="openOlxAuthorizeUrl"
              @sync="runPortalSync"
            />
          </LaisCard>

          <ImovelHighlightCard
            v-if="isDetailSectionVisible('publicacao') && canManageHighlight"
            v-model:highlight-form="highlightForm"
            :status-label="highlightStatusLabel"
            :status-tone="highlightStatusTone"
            :scheduled-for-label="highlightScheduledForLabel"
            :schedule="highlightSchedule"
            :provider-name="highlightProviderName"
            :placement-label="highlightPlacementLabel"
            :min-date-time="highlightMinDateTime"
            :submitting="highlightSubmitting"
            :error-message="highlightError"
            :is-valid="isHighlightFormValid"
            @submit="openHighlightConfirm"
          />

          <ImovelReportShareCard
            v-if="activeSection === 'resumo'"
            :report-link="reportLink"
            @copy-link="copiarLinkAvaliacao"
            @share-whatsapp="compartilharAvaliacaoWhatsapp"
          />

          <ImovelFeaturesCard
            v-if="isDetailSectionVisible('caracteristicas')"
            :imovel="imovel"
            :type-label="typeLabel"
            :status-label="statusLabel"
          />

          <LaisCard
            v-if="isDetailSectionVisible('publicacao') && imovel.linkExterno"
            title="Link publicado"
            density="tight"
          >
            <a
              :href="imovel.linkExterno"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 text-[13px] text-lais-link hover:underline break-all"
            >
              <span
                class="i-lucide-external-link size-3.5"
                aria-hidden="true"
              />
              {{ imovel.linkExterno }}
            </a>
          </LaisCard>

          <LaisCard v-if="isDetailSectionVisible('endereco')" title="Endereço">
            <p class="text-[14px] text-lais-text-body leading-relaxed">
              <span>{{ addressLabel }}</span>
              <br v-if="imovel.cep" />
              <span v-if="imovel.cep" class="text-lais-text-muted text-[12px]">
                CEP: {{ imovel.cep }}
              </span>
            </p>
          </LaisCard>

          <ImovelConversionActionsCard
            v-if="activeSection === 'resumo'"
            :imovel="imovel"
            :report-link="reportLink"
            :contact-id="activeContactId"
            :business-context="ownerBusinessContext"
            :favorited="listingFavorited"
            :favorite-id="listingFavoriteId"
            @shared="handleListingShared"
            @share-error="handleListingShareError"
            @toggle-favorite="handleFavoriteToggle"
            @action="handleListingCtaAction"
          />
        </aside>
      </div>

      <div
        v-if="activeSection === 'resumo'"
        id="similar-listings-section"
        class="scroll-mt-6 px-4 pb-8 sm:px-6 lg:px-10"
      >
        <SimilarListings
          :imovel-id="imovel.id"
          :account-id="accountId"
          kind="neighbourhood"
          @card-click="
            payload =>
              trackListingAction('listing.similar_card_clicked', payload)
          "
        />
      </div>
    </div>

    <ImageLightbox
      v-model:open="lightboxOpen"
      :items="galleryItems"
      :tabs="mediaTabs"
      :initial-index="midiaIndex"
      :initial-tab="lightboxTab"
      :title="imovel?.titulo || 'Galeria de fotos'"
      @change="handleLightboxChange"
    />

    <KeyCustodyDrawer
      :show="showKeyCustodyDrawer"
      :imovel-id="imovel?.id"
      side="right"
      @close="showKeyCustodyDrawer = false"
      @changed="handleKeyCustodyChanged"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="excluir"
      title="Excluir imóvel"
      message="Deseja realmente excluir"
      :message-value="` ${imovel?.titulo || 'este imóvel'}?`"
      confirm-text="Excluir"
      reject-text="Cancelar"
    />

    <LaisConfirmModal
      :show="showPriceEstimateConfirm"
      title="Aplicar estimativa QPreço"
      :description="priceEstimateConfirmDescription"
      confirm-text="Aplicar estimativa"
      cancel-text="Cancelar"
      confirm-variant="primary"
      :is-loading="priceEstimateApplying"
      @close="closePriceEstimateConfirm"
      @confirm="confirmPriceEstimateApplication"
    />

    <LaisConfirmModal
      :show="showHighlightConfirm"
      :title="t('PROPERTIES.HIGHLIGHT.CONFIRM.TITLE')"
      :description="highlightConfirmDescription"
      :confirm-text="t('PROPERTIES.HIGHLIGHT.CONFIRM.CONFIRM')"
      :cancel-text="t('PROPERTIES.HIGHLIGHT.CONFIRM.CANCEL')"
      confirm-variant="primary"
      icon="i-lucide-sparkles"
      :is-loading="highlightSubmitting"
      @close="closeHighlightConfirm"
      @confirm="submitHighlight"
    />
  </section>
</template>
